import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Calculator, Settings as SettingsIcon, ChevronDown } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getLanguageName } from '../constants/languages';
import { computePriceFromRates, formatMoney } from '../utils/rateCalc';
import './PricePanel.css';

function formatNum(n) {
  return Math.round(n).toLocaleString();
}

// Shown after a translation: the source/target character counts (the thing a
// translator eyeballing a client's photo can't see), and - if they've set
// their own rates in Settings - what the job would cost at THAT rate. The price
// is their client rate, never what they paid us.
function PricePanel({ sourceChars, targetChars, sourceWords, targetWords, sourceLang, targetLang }) {
  const { t } = useTranslation('chat');
  const { user } = useAuth();
  const rs = user?.rate_settings || null;
  // Figures collapsed by default: a visible price reads like a ChatsControl
  // charge. The title frames it as the translator's own client quote; they
  // expand to see counts + price.
  const [open, setOpen] = useState(false);

  const calc = useMemo(
    () =>
      computePriceFromRates({
        rateSettings: rs,
        sourceChars,
        targetChars,
        sourceWords,
        targetWords,
        sourceLang,
        targetLang,
      }),
    [rs, sourceChars, targetChars, sourceWords, targetWords, sourceLang, targetLang]
  );

  return (
    <div className={`price-panel sidebar-card ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="price-panel-summary"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="price-panel-body"
      >
        <Calculator size={16} />
        <span className="price-panel-summary-text">{t('pricePanel.title')}</span>
        <ChevronDown size={16} className="price-panel-chevron" />
      </button>

      {open && (
      <div className="price-panel-body" id="price-panel-body">
      <div className="price-counts">
        <div className="price-count">
          <span className="price-count-value">{sourceChars ? formatNum(sourceChars) : '-'}</span>
          <span className="price-count-label">{t('pricePanel.input')}</span>
        </div>
        <div className="price-count">
          <span className="price-count-value">{targetChars ? formatNum(targetChars) : '-'}</span>
          <span className="price-count-label">{t('pricePanel.output')}</span>
        </div>
      </div>

      {calc && calc.rate && calc.price != null ? (
        <div className="price-result">
          <div className="price-amount">
            {formatMoney(calc.price)} {calc.currency}
          </div>
          <div className="price-breakdown">
            {calc.unit === 'word'
              ? t('pricePanel.breakdownWord', {
                  words: formatNum(calc.quantity),
                  rate: formatMoney(calc.rate.price),
                  currency: calc.currency,
                  basis: t(`pricePanel.basis.${calc.basis}`),
                })
              : t('pricePanel.breakdown', {
                  pages: calc.quantity.toFixed(1),
                  rate: formatMoney(calc.rate.price),
                  currency: calc.currency,
                  basis: t(`pricePanel.basis.${calc.basis}`),
                })}
          </div>
          <p className="price-disclaimer">{t('pricePanel.disclaimer')}</p>
        </div>
      ) : (
        <Link to="/settings#rates" className="price-set-rate">
          <SettingsIcon size={14} />
          {calc && !calc.rate
            ? t('pricePanel.noRateForPair', {
                pair: `${getLanguageName(sourceLang)} → ${getLanguageName(targetLang)}`,
              })
            : t('pricePanel.setRates')}
        </Link>
      )}
      </div>
      )}
    </div>
  );
}

export default PricePanel;
